<script setup lang="ts">
import { computed } from 'vue';
import type { Component } from 'vue';
import * as LucideIcons from 'lucide-vue-next';
import {
  CircleCheckBig,
  Home,
  Info,
  Building2,
  FileCheck,
  BookOpen,
  Megaphone,
  GraduationCap,
  Briefcase,
  Users,
  Quote,
} from 'lucide-vue-next';

const BuildingIcon = (LucideIcons as Record<string, Component>).Building;
const MessageSquareIcon =
  (LucideIcons as Record<string, Component>).MessageSquare;
const FileTextIcon =
  (LucideIcons as Record<string, Component>).FileText ??
  (LucideIcons as Record<string, Component>).FileTextIcon;
const DownloadIcon = (LucideIcons as Record<string, Component>).Download;
const PenSquareIcon = (LucideIcons as Record<string, Component>).PenSquare;
const ClipboardCheckIcon =
  (LucideIcons as Record<string, Component>).ClipboardCheck;
const AwardIcon = (LucideIcons as Record<string, Component>).Award;
const ArrowRightIcon =
  (LucideIcons as Record<string, Component>).ArrowRight ??
  (LucideIcons as Record<string, Component>).ArrowRightIcon;

const PROCESS_ICON_MAP: Record<string, Component> = {
  'circle-check-big': CircleCheckBig,
  home: Home,
  info: Info,
  building: BuildingIcon ?? Building2,
  'building-2': Building2,
  'file-check': FileCheck,
  'file-text': FileTextIcon ?? FileCheck,
  'book-open': BookOpen,
  'message-square': MessageSquareIcon ?? Info,
  megaphone: Megaphone,
  'graduation-cap': GraduationCap,
  briefcase: Briefcase,
  users: Users,
  quote: Quote,
  download: DownloadIcon ?? FileCheck,
  'pen-square': PenSquareIcon ?? FileCheck,
  'clipboard-check': ClipboardCheckIcon ?? FileCheck,
  award: AwardIcon ?? FileCheck,
};

type StepColour = { bg: string; icon: string };

const DEFAULT_STEP_COLOUR: StepColour = {
  bg: 'rgba(4, 120, 87, 0.1)',
  icon: '#047857',
};

const COLOUR_MAP: Record<string, StepColour> = {
  primary: DEFAULT_STEP_COLOUR,
  secondary: { bg: 'rgba(124, 58, 237, 0.1)', icon: '#7c3aed' },
  tertiary: { bg: 'rgba(234, 88, 12, 0.1)', icon: '#ea580c' },
  quaternary: { bg: 'rgba(8, 145, 178, 0.1)', icon: '#0891b2' },
  quinary: { bg: 'rgba(217, 119, 6, 0.1)', icon: '#d97706' },
};

function iconComponent(iconName: string): Component | null {
  const key = iconName?.trim() ?? '';
  if (key && PROCESS_ICON_MAP[key]) return PROCESS_ICON_MAP[key];
  return PROCESS_ICON_MAP['file-text'] ?? null;
}

function stepColour(colourName: string): StepColour {
  const key = colourName?.trim() ?? '';
  return COLOUR_MAP[key] ?? DEFAULT_STEP_COLOUR;
}

const props = withDefaults(
  defineProps<{
    title?: string;
    steps?: Array<{
      title: string;
      description: string;
      icon: string;
      colour: string;
    }>;
  }>(),
  { title: '', steps: () => [] }
);

const stepList = computed(() => Array.isArray(props.steps) ? props.steps : []);
</script>

<template>
  <div v-if="stepList.length >= 2" class="process-overview">
    <h2 class="process-overview__title">{{ title }}</h2>
    <div class="process-overview__steps">
      <template v-for="(step, index) in stepList" :key="index">
        <div class="process-overview__step">
          <div
            class="process-overview__icon-wrap"
            :style="{
              backgroundColor: stepColour(step.colour).bg,
            }"
          >
            <component
              :is="iconComponent(step.icon)"
              class="process-overview__icon"
              :style="{ color: stepColour(step.colour).icon }"
              aria-hidden="true"
            />
          </div>
          <div class="process-overview__card">
            <div
              class="process-overview__step-label"
              :aria-label="`Step ${index + 1}`"
            >
              STEP {{ index + 1 }}
            </div>
            <h3 class="process-overview__step-title">{{ step.title }}</h3>
            <p class="process-overview__step-desc">{{ step.description }}</p>
          </div>
        </div>
        <div
          v-if="index < stepList.length - 1"
          class="process-overview__arrow"
          aria-hidden="true"
        >
          <component :is="ArrowRightIcon" class="process-overview__arrow-icon" />
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
/* Mobile-first: matches Prototype Licensing process flow (gap-3, p-4, text-lg, etc.) */
.process-overview {
  background-color: #ffffff;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  padding: 1rem;
  overflow: hidden;
  margin-bottom: 1.5rem;
}

/* Prototype: text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-4 sm:mb-6 md:mb-8 text-center */
.process-overview__title {
  font-size: 1.125rem;
  line-height: 1.75rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem;
  text-align: center;
}

.process-overview__steps {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  max-width: 100%;
}

/* On mobile: no flex-grow so steps stack at content height; on lg row they flex equally */
.process-overview__step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  min-width: 0;
}

/* w-14 h-14 on mobile (3.5rem), mb-3 (0.75rem) */
.process-overview__icon-wrap {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  flex-shrink: 0;
}

/* w-7 h-7 (1.75rem) on mobile */
.process-overview__icon {
  width: 1.75rem;
  height: 1.75rem;
}

/* bg-gray-50, rounded-lg, p-4, min-h-[120px], border-gray-200, shadow-sm */
.process-overview__card {
  background-color: #f9fafb;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  border: 1px solid #e5e7eb;
  width: 100%;
  min-height: 120px;
  display: flex;
  flex-direction: column;
}

/* text-sm font-bold mb-1.5 */
.process-overview__step-label {
  font-size: 0.875rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.375rem;
}

/* font-semibold text-sm mb-1.5 */
.process-overview__step-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.375rem;
}

/* text-xs text-gray-700 leading-tight */
.process-overview__step-desc {
  font-size: 0.75rem;
  line-height: 1.25;
  color: #374151;
  margin: 0;
  flex: 1;
}

.process-overview__arrow {
  display: none;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.process-overview__arrow-icon {
  width: 1.25rem;
  height: 1.25rem;
  color: #4b5563;
}

@media (min-width: 640px) {
  .process-overview {
    border-radius: 1rem;
    padding: 1.5rem;
  }

  .process-overview__title {
    font-size: 1.25rem;
    line-height: 1.75rem;
    margin-bottom: 1.5rem;
  }

  .process-overview__steps {
    gap: 1rem;
  }

  .process-overview__icon-wrap {
    width: 4rem;
    height: 4rem;
    margin-bottom: 0.75rem;
  }

  .process-overview__icon {
    width: 2rem;
    height: 2rem;
  }
}

@media (min-width: 768px) {
  .process-overview {
    padding: 2rem;
  }

  .process-overview__title {
    font-size: 1.5rem;
    line-height: 2rem;
    margin-bottom: 2rem;
  }
}

@media (min-width: 1024px) {
  .process-overview__steps {
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
  }

  .process-overview__step {
    flex: 1 1 auto;
    max-width: 160px;
    width: auto;
  }

  .process-overview__icon-wrap {
    width: 3.5rem;
    height: 3.5rem;
    margin-bottom: 0.75rem;
  }

  .process-overview__icon {
    width: 1.75rem;
    height: 1.75rem;
  }

  .process-overview__arrow {
    display: flex;
  }
}

@media (min-width: 1280px) {
  .process-overview__steps {
    gap: 1rem;
  }

  .process-overview__step {
    max-width: 180px;
  }

  .process-overview__icon-wrap {
    width: 4rem;
    height: 4rem;
    margin-bottom: 1rem;
  }

  .process-overview__icon {
    width: 2rem;
    height: 2rem;
  }
}
</style>
